import { useCallback, useEffect, useState } from 'react';

export type PushSubscriptionState =
  'unsupported' | 'unsubscribed' | 'subscribed' | 'pending' | 'denied';

/** Mirrors PUSH_TOPICS in netlify/functions/_shared/pushSubscriptionStore.ts. */
export type PushTopic = 'btc15' | 'crash-bounce' | 'breakout-watch' | 'listing-radar';

function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function postJson(url: string, body: unknown): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** The topics the server has for this device; empty if it can't say. */
async function fetchTopics(endpoint: string): Promise<string[]> {
  try {
    const res = await postJson('/api/push-topics', { endpoint });
    if (!res.ok) return [];
    const data = (await res.json()) as { topics?: unknown };
    return Array.isArray(data.topics) ? data.topics : [];
  } catch {
    return [];
  }
}

/**
 * Adapted from `notifier`'s client/usePushSubscription (github:Reints-Labs-LLC/notifier) —
 * same subscribe/unsubscribe flow, but pointed at this repo's own flat,
 * hyphenated Netlify Functions (push-subscribe/push-unsubscribe/push-test/
 * push-topics) instead of the package's default `/api/push/*` nested-route
 * convention.
 *
 * A browser has one push subscription per site, shared by every page's
 * opt-in, so each opt-in turns its own `topic` on or off on the server and
 * the browser subscription only ends when no topic is left.
 */
export function usePushSubscription(vapidPublicKey: string, topic: PushTopic) {
  const [state, setState] = useState<PushSubscriptionState>('pending');

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    let cancelled = false;
    navigator.serviceWorker.getRegistration().then(async (reg) => {
      const sub = await reg?.pushManager.getSubscription();
      const on = sub ? (await fetchTopics(sub.endpoint)).includes(topic) : false;
      if (!cancelled) setState(on ? 'subscribed' : 'unsubscribed');
    });
    return () => {
      cancelled = true;
    };
  }, [topic]);

  const subscribe = useCallback(async () => {
    const registration = await navigator.serviceWorker.register('/sw.js');
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      setState('denied');
      return;
    }

    // Reuses this browser's existing subscription if another page made one.
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
      }));

    await postJson('/api/push-subscribe', { ...subscription.toJSON(), topic });
    await postJson('/api/push-test', { ...subscription.toJSON(), topic });

    setState('subscribed');
  }, [vapidPublicKey, topic]);

  const unsubscribe = useCallback(async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      setState('unsubscribed');
      return;
    }

    const res = await postJson('/api/push-unsubscribe', {
      endpoint: subscription.endpoint,
      topic,
    });
    const data = res.ok ? ((await res.json()) as { topics?: unknown }) : {};
    const remaining = Array.isArray(data.topics) ? data.topics : [];
    if (remaining.length === 0) await subscription.unsubscribe();
    setState('unsubscribed');
  }, [topic]);

  return { state, subscribe, unsubscribe };
}
